import type { ReplayTeamColors } from "@Components/game/session-results/feedback/visualization/constants";
import {
  GOAL_ZONES,
  PITCH_DIMENSIONS,
  TEAM_COLORS,
  VISUALIZATION_LAYOUT,
} from "@Components/game/session-results/feedback/visualization/constants";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics } from "@pixi/react";
import { useCallback } from "react";

interface IFootballPitch {
  teamColors?: ReplayTeamColors;
}

const FootballPitch = ({ teamColors = TEAM_COLORS }: IFootballPitch) => {
  const topLeftX = VISUALIZATION_LAYOUT.pitch.x;
  const topLeftY = VISUALIZATION_LAYOUT.pitch.y;

  const drawPitchLines = useCallback((g: PixiGraphics) => {
    g.clear();
    g.lineStyle(1.5, 0xffffff, 0.7);

    g.drawRoundedRect(topLeftX, topLeftY, PITCH_DIMENSIONS.WIDTH, PITCH_DIMENSIONS.HEIGHT, PITCH_DIMENSIONS.CORNER_RADIUS);

    // Center line
    g.moveTo(topLeftX + PITCH_DIMENSIONS.WIDTH / 2, topLeftY);
    g.lineTo(topLeftX + PITCH_DIMENSIONS.WIDTH / 2, topLeftY + PITCH_DIMENSIONS.HEIGHT);

    // Center circle
    g.drawCircle(topLeftX + PITCH_DIMENSIONS.WIDTH / 2, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2, (9.15 / 105) * PITCH_DIMENSIONS.WIDTH);

    // Center spot
    g.beginFill(0xffffff, 0.7);
    g.drawCircle(topLeftX + PITCH_DIMENSIONS.WIDTH / 2, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2, 3);
    g.endFill();

    // Penalty areas (18-yard boxes)
    const penaltyAreaWidth = (16.5 / 105) * PITCH_DIMENSIONS.WIDTH;
    const penaltyAreaDepth = (40.32 / 68) * PITCH_DIMENSIONS.HEIGHT;

    // Left penalty area
    g.drawRect(topLeftX, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - penaltyAreaDepth / 2, penaltyAreaWidth, penaltyAreaDepth);

    // Right penalty area
    g.drawRect(
      topLeftX + PITCH_DIMENSIONS.WIDTH - penaltyAreaWidth,
      topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - penaltyAreaDepth / 2,
      penaltyAreaWidth,
      penaltyAreaDepth
    );

    // Goal areas (6-yard boxes)
    const goalAreaWidth = (5.5 / 105) * PITCH_DIMENSIONS.WIDTH;
    const goalAreaDepth = (18.32 / 68) * PITCH_DIMENSIONS.HEIGHT;

    // Left goal area
    g.drawRect(topLeftX, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - goalAreaDepth / 2, goalAreaWidth, goalAreaDepth);

    // Right goal area
    g.drawRect(
      topLeftX + PITCH_DIMENSIONS.WIDTH - goalAreaWidth,
      topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - goalAreaDepth / 2,
      goalAreaWidth,
      goalAreaDepth
    );

    // Penalty spots
    const penaltySportDistance = (11 / 105) * PITCH_DIMENSIONS.WIDTH;
    const leftPenaltySpotX = topLeftX + penaltySportDistance;
    const rightPenaltySpotX = topLeftX + PITCH_DIMENSIONS.WIDTH - penaltySportDistance;
    const penaltySpotY = topLeftY + PITCH_DIMENSIONS.HEIGHT / 2;

    // Left penalty spot
    g.beginFill(0xffffff, 0.7);
    g.drawCircle(leftPenaltySpotX, penaltySpotY, 3);
    g.endFill();

    // Right penalty spot
    g.beginFill(0xffffff, 0.7);
    g.drawCircle(rightPenaltySpotX, penaltySpotY, 3);
    g.endFill();

    // Penalty arcs (9.5m radius from penalty spot)
    const penaltyArcRadius = (9.5 / 105) * PITCH_DIMENSIONS.WIDTH;
    const distanceToBoundary = penaltyAreaWidth - penaltySportDistance;
    const intersectionAngle = Math.acos(distanceToBoundary / penaltyArcRadius);

    const getArc = (penaltySpotX: number, baseAngle: number) => {
      const startAngle = baseAngle - intersectionAngle;
      const endAngle = baseAngle + intersectionAngle;

      return {
        startAngle,
        endAngle,
        startX: penaltySpotX + penaltyArcRadius * Math.cos(startAngle),
        startY: penaltySpotY + penaltyArcRadius * Math.sin(startAngle),
      };
    };

    const left = getArc(leftPenaltySpotX, 0);
    const right = getArc(rightPenaltySpotX, Math.PI);

    g.moveTo(left.startX, left.startY);
    g.arc(leftPenaltySpotX, penaltySpotY, penaltyArcRadius, left.startAngle, left.endAngle);

    g.moveTo(right.startX, right.startY);
    g.arc(rightPenaltySpotX, penaltySpotY, penaltyArcRadius, right.startAngle, right.endAngle);
  }, []);

  return (
    <Container x={0} y={0}>
      <Container x={0} y={0}>
        <Graphics draw={drawPitchLines} />
      </Container>

      {GOAL_ZONES.map((_, index) => {
        const isLeftGoal = index === 0;
        const teamColor = isLeftGoal ? teamColors.home.primary : teamColors.away.primary;

        const drawGoalBox = (g: PixiGraphics) => {
          g.clear();

          const goalWidth = (2.44 / 105) * PITCH_DIMENSIONS.WIDTH;
          const goalHeight = (7.32 / 68) * PITCH_DIMENSIONS.HEIGHT;
          const lineWidth = 3;

          g.beginFill(teamColor, 0.25);
          if (isLeftGoal) {
            g.drawRect(topLeftX - goalWidth, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - goalHeight / 2, goalWidth, goalHeight);
          } else {
            g.drawRect(topLeftX + PITCH_DIMENSIONS.WIDTH, topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - goalHeight / 2, goalWidth, goalHeight);
          }
          g.endFill();

          g.lineStyle(lineWidth, teamColor, 0.8);

          const multiplier = isLeftGoal ? -1 : 1;
          const baseX = isLeftGoal ? topLeftX : topLeftX + PITCH_DIMENSIONS.WIDTH;
          const goalStartX = baseX + multiplier * goalWidth;
          const goalEndX = baseX;
          const goalTopY = topLeftY + PITCH_DIMENSIONS.HEIGHT / 2 - goalHeight / 2;
          const goalBottomY = goalTopY + goalHeight;

          g.moveTo(goalStartX, goalTopY);
          g.lineTo(goalStartX, goalBottomY);
          g.lineTo(goalEndX, goalBottomY);
          g.moveTo(goalStartX, goalTopY);
          g.lineTo(goalEndX, goalTopY);
        };

        return (
          <Container key={index} x={0} y={0}>
            <Graphics draw={drawGoalBox} />
          </Container>
        );
      })}
    </Container>
  );
};

export default FootballPitch;
